import { ApiRawModel, RawModel, RawModelUploadStatus } from '@/types/rawModel';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MAIN_ASSET_QUERY_KEY, useAssetsParams, useAssetsQueryKey } from '../../../hooks/useAssetsQuery';
import { rawModelFinishedStates } from '@/app/assets/actions/utils';
import { getRawModelInPendingStates } from '@/app/assets/actions/rawModel';
import { useInterval } from '@mantine/hooks';
import { useOnboarding } from '@/app/contexts/OnboardingContext/OnboardingContext';
import { OnboardingFlows } from '@/app/contexts/OnboardingContext/constants';
import useQueryCallback from '../../../../../shared/useQueryCallback';

export function useUploadPendingAssets(
  assets: RawModel[],
  updateRawModelState: (args: { id: number; state: RawModelUploadStatus }) => void
) {
  const [pendingRawModels, setPendingRawModels] = useState<ApiRawModel[]>([]);
  const queryClient = useQueryClient();
  const [shouldFetch, setShouldFetch] = useState(false);
  const { runFlow } = useOnboarding();
  const { page, query, onlyOutputs } = useAssetsParams();
  const assetsQueryKey = useAssetsQueryKey(query, page, onlyOutputs);
  const { addCallback } = useQueryCallback(assetsQueryKey);

  const intervalFunction = async () => {
    const rawModelsInPendingState = await getRawModelInPendingStates();
    rawModelsInPendingState?.forEach((rawModel) =>
      updateRawModelState({ id: rawModel.id, state: rawModel.upload_status })
    );

    const finishedPendingRawModel = pendingRawModels.find(
      (oldPendingRawModel) =>
        !rawModelsInPendingState.find((freshPendingRawModel) => freshPendingRawModel.id === oldPendingRawModel.id)
    );

    const hasAnyPending = !!rawModelsInPendingState.length;
    setShouldFetch(hasAnyPending);
    if (!hasAnyPending || finishedPendingRawModel) {
      addCallback(() => {
        runFlow(OnboardingFlows.ASSET_MORE_INFO, { id: finishedPendingRawModel?.id });
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(MAIN_ASSET_QUERY_KEY),
      });
    }
    setPendingRawModels(rawModelsInPendingState);
  };

  const { start, stop } = useInterval(intervalFunction, 3000);

  useEffect(() => {
    const hasPendingAssets = assets.some((asset) => !rawModelFinishedStates.has(asset.upload_status));

    if (!hasPendingAssets) {
      return;
    }

    start();
  }, [assets, queryClient, start]);

  useEffect(() => {
    if (shouldFetch) {
      return;
    }

    stop();
  }, [stop, shouldFetch]);

  useEffect(() => {
    return () => stop();
  }, [stop]);
}
